import { AccountService } from '../providers';
import { AppRoute } from '../enums';
import { Directive, OnDestroy, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ServerResponse } from '../interfaces';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { finalize, map, takeUntil } from 'rxjs/operators';

@Directive()
export abstract class SignupBaseComponent<TLoader> implements OnDestroy {
  public response: WritableSignal<ServerResponse | null> = signal(null);
  public showCountdown = signal(false);
  public showSuccess = signal(false);
  public showForm: Signal<boolean> = computed(() => !this.showCountdown() && !this.showSuccess());
  public showResponseMessage: Signal<boolean> = computed(
    () => !this.showSuccess() && !!this.response()?.show && !this.showCountdown(),
  );

  public signupForm: FormGroup = this.createForm();

  protected readonly router: Router = inject(Router);
  protected readonly accountService: AccountService = inject(AccountService);
  protected readonly translate: TranslateService = inject(TranslateService);

  private destroyed$ = new Subject<void>();

  public goToNextStep(): void {
    this.router.navigate([AppRoute.EmailValidation]);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  protected abstract createForm(): FormGroup;

  protected abstract endLoader(loader: TLoader): Promise<void>;

  protected abstract startLoader(): Promise<TLoader>;

  protected async submitSignUp(email: string, personalNumber: string): Promise<void> {
    if (!email || !personalNumber) {
      return;
    }

    const loader = await this.startLoader();
    this.accountService
      .createUser({
        email,
        personalNumber,
      })
      .pipe(
        map((response) => {
          if (!response.success && response.data > 0) {
            throw response;
          }

          return response.success;
        }),
        takeUntil(this.destroyed$),
        finalize(async () => await this.endLoader(loader)),
      )
      .subscribe({
        next: async (value: boolean) => {
          if (value) {
            this.setSuccess();
          }
        },
        error: async (error) => {
          this.setResponse(error);
        },
      });
  }

  protected setResponse(value: ServerResponse): void {
    if (!value) {
      this.response.set({
        id: '',
        message: '',
        success: false,
        show: false,
        href: '',
        linktext: '',
      });

      return;
    }

    const { href, linktext } = this.evaluateServerResponse(value);
    this.response.set({
      ...value,
      href,
      linktext,
      show: value && value.message !== null,
      message: this.translate.instant(value.message, { id: value.id }),
    });
  }

  private setSuccess(): void {
    this.showSuccess.set(true);
    this.showCountdown.set(false);
  }

  private evaluateServerResponse(response: ServerResponse): { href: string; linktext: string } {
    switch (response.message) {
      case 'server.success.register.IsAlreadyRegistredAndNotValidated':
        return {
          href: `/${AppRoute.Settings}/${AppRoute.NewValidationLink}/`,
          linktext: 'Bitte beantragen Sie hier eine neue Validierungs-E-Mail.',
        };
      case 'server.success.register.AlreadyRegistredWithOtherEmailAndNotValidated': {
        const routeWithParam = AppRoute.AutoChangeEmail;
        const finalPath = routeWithParam.replace(':id', response.id);

        return {
          href: `/${AppRoute.Settings}/${finalPath}`,
          linktext: 'Wenn Sie Ihre E-Mail ändern möchten klicken Sie bitte auf diesen Link.',
        };
      }

      default:
        return {
          href: '',
          linktext: '',
        };
    }
  }
}
